import { ACH_LEVEL_KEYS, AchLevelKey, AchRarity, CardTarget } from "../model/types";

/**
 * Атрибут, по которому AchievementCardRoot узнаёт любую картинку шкалы или
 * достижения (PLAN §3.8): JSON цели карточки. Этот модуль — в основном бандле
 * (его читает Root), поэтому каталог сюда не импортируется.
 */
export const ACH_CARD_ATTR = "data-ach-card";

export const encodeCardTarget = (target: CardTarget): string => JSON.stringify(target);

const KEY_RE = /^[a-z0-9_]{1,64}$/;
/** Упражнение картинки лестницы тренировок: water, pose, tree, bath, hot. */
const VARIANT_RE = /^[a-z]{1,16}$/;

const int = (v: unknown, lo: number, hi: number): number | undefined =>
    typeof v === "number" && Number.isInteger(v) && v >= lo && v <= hi ? v : undefined;

const num = (v: unknown): number | undefined => (typeof v === "number" && Number.isFinite(v) ? v : undefined);

const str = (v: unknown, max: number): string | undefined =>
    typeof v === "string" && v.trim() ? v.trim().slice(0, max) : undefined;

const flag = (v: unknown): 1 | undefined => (v === 1 || v === true ? 1 : undefined);

const form = (v: unknown): "f" | "m" | undefined => (v === "f" || v === "m" ? v : undefined);

const rarity = (v: unknown): AchRarity | undefined => int(v, 1, 4) as AchRarity | undefined;

/** Доля участников, % (0 < N ≤ 100): «есть у N % участников». */
const share = (v: unknown): number | undefined => {
    const n = num(v);
    return n !== undefined && n > 0 && n <= 100 ? n : undefined;
};

const DATE_RE = /^\d{4}-\d{2}-\d{2}/;

/** «ГГГГ-ММ-ДД» (или ISO-время — его день). */
const day = (v: unknown): string | undefined => (typeof v === "string" && DATE_RE.test(v) ? v.slice(0, 10) : undefined);

/** Без пустых полей: {t, k} вместо {t, k, tier: undefined, …}. */
const compact = <T extends object>(o: T): T => {
    const out = {} as Record<string, unknown>;
    Object.entries(o).forEach(([k, v]) => {
        if (v !== undefined) out[k] = v;
    });
    return out as T;
};

/**
 * Разбор атрибута. Битый JSON, чужой вид цели, неверные поля — null (карточка
 * не открывается, нажатие проходит как обычно). Незнакомые поля отбрасываются.
 * Ключи, которых нет в каталоге, отсеивает уже модель карточки.
 */
export const parseCardTarget = (raw: string | null | undefined): CardTarget | null => {
    if (!raw) return null;
    let o: Record<string, unknown>;
    try {
        const v = JSON.parse(raw);
        if (!v || typeof v !== "object" || Array.isArray(v)) return null;
        o = v as Record<string, unknown>;
    } catch {
        return null;
    }
    const self = flag(o.self);
    const lock = flag(o.lock);
    const cur = int(o.cur, 0, 99);
    if (o.t === "lvl") {
        const s = o.s as AchLevelKey;
        const n = int(o.n, 1, 99);
        if (!ACH_LEVEL_KEYS.includes(s) || !n) return null;
        const x = typeof o.x === "string" && VARIANT_RE.test(o.x) ? o.x : undefined;
        return compact({ t: "lvl" as const, s, n, f: form(o.f), x, self, pts: num(o.pts), v: num(o.v), lock, cur });
    }
    if (o.t !== "ach" && o.t !== "top") return null;
    const k = typeof o.k === "string" && KEY_RE.test(o.k) ? o.k : "";
    if (!k) return null;
    const tier = int(o.tier, 1, 99);
    const v = num(o.v);
    // Окно v2: редкость, доля и дата взятого — у ещё не полученного их нет.
    const earned = lock ? {} : { r: rarity(o.r), sh: share(o.sh), at: day(o.at) };
    if (o.t === "top") return compact({ t: "top" as const, k, tier, self, v, lock, cur, f: form(o.f), ...earned });
    return compact({
        t: "ach" as const,
        k,
        tier,
        self,
        v,
        lock,
        cur,
        y: int(o.y, 1900, 3000),
        e: str(o.e, 160),
        c: str(o.c, 100),
        note: str(o.note, 200),
        ...earned,
    });
};
